<template>
    <div>
        <h1>Request Data</h1>
        <ul v-if="errors.length" class="error">
            <li v-for="error in errors" v-bind:key="error">
                {{ error }}
            </li>
        </ul>
        <form
            v-if="isInitial || isSaving"
            @submit.prevent="request"
            novalidate="true"
        >
            <div>
                <label for="project-group">Project Group</label>
                <select
                    id="project-group"
                    v-model="projectGroupId"
                    @change="projectGroupChange($event.target.value)"
                >
                    <option value="0">Select Project Group</option>
                    <option
                        v-for="option in projectGroups"
                        v-bind:key="option.key"
                        :value="option.id"
                    >
                        {{ option.name }}
                    </option>
                </select>
            </div>
            <div>
                <label for="project">Project</label>
                <select
                    id="project"
                    v-model="projectReportId"
                    :disabled="projectsDisabled"
                >
                    <option value="0">All Projects</option>
                    <option
                        v-for="option in projects"
                        v-bind:key="option.key"
                        :value="option.id"
                    >
                        {{ option.name }}
                    </option>
                </select>
            </div>
            <div>
                <label for="name">
                    Name*
                </label>
                <input v-model="name" type="text" id="name" name="name" value />
            </div>

            <div>
                <label for="email">
                    Email*
                </label>
                <input
                    v-model="email"
                    type="email"
                    id="email"
                    name="email"
                    value
                />
            </div>

            <div>
                <label for="orgTitle">
                    Organizational Title*
                </label>
                <input
                    v-model="orgTitle"
                    type="text"
                    id="orgTitle"
                    name="orgTitle"
                    value
                />
            </div>

            <div>
                <label for="affiliation">
                    Institutional Affiliation*
                </label>
                <input
                    v-model="affiliation"
                    type="text"
                    id="affiliation"
                    name="affiliation"
                    value
                />
            </div>

            <div>
                <label for="start-month">Data Range Start Date</label>
                <select v-model="startMonth" id="start-month">
                    <option disabled value="">Year</option>
                    <option
                        v-for="option in monthOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
                <select v-model="startDay" id="start-day">
                    <option disabled value="">Day</option>
                    <option
                        v-for="option in dayOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
                <select v-model="startYear" id="start-year">
                    <option disabled value="">Year</option>
                    <option
                        v-for="option in yearOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div>
                <label for="end-month">Data Range End Date</label>
                <select v-model="endMonth" id="end-month">
                    <option disabled value="">Year</option>
                    <option
                        v-for="option in monthOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
                <select v-model="endDay" id="end-day">
                    <option disabled value="">Day</option>
                    <option
                        v-for="option in dayOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
                <select v-model="endYear" id="end-year">
                    <option disabled value="">Year</option>
                    <option
                        v-for="option in yearOptions"
                        v-bind:key="option.key"
                        v-bind:value="option.value"
                    >
                        {{ option.text }}
                    </option>
                </select>
            </div>

            <div>
                <label for="comments">
                    Please outline the nature and topic of the intended
                    research*
                </label>
                <textarea v-model="comments" name="comments" id="comments">
                </textarea>
            </div>

            <div class="terms" v-html="termsOfUse">
                <!-- eslint-disable-next-line -->
                <!-- <p>The researchers agree to acknowledge Journey North (and Journey North volunteers) in all relevant publications. In addition, the following statement must be included in the acknowledgment section of any publication:</p> -->
                <!-- eslint-disable-next-line -->
                <!-- <blockquote><p>The data were used with permission from Journey North. We thank Journey North for supplying data. We also thank all the volunteer participants who gathered data for the project.</p></blockquote> -->
                <!-- eslint-disable-next-line -->
                <!-- <p>If data retrieved from Journey North are to be used for web-based products (maps, summaries, etc.), please contact Journey North for instructions on applicable logo and website links.</p> -->
            </div>

            <div>
                <button type="submit" name="button">Submit Message</button>
            </div>
        </form>
        <div v-if="isSuccess">
            <h2>Request Sent</h2>
            <p>
                We&rsquo;ll be in touch.
            </p>
        </div>
        <div v-if="isFailed">
            <h2>Request Failed</h2>
            <p>
                <a href="javascript:void(0)" @click="reset()">Try again</a>
            </p>
            <p>{{ requestError }}</p>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
const STATUS_INITIAL = 0,
    STATUS_SAVING = 1,
    STATUS_SUCCESS = 2,
    STATUS_FAILED = 3
const FIELD_DISABLED = 0,
    FIELD_ENABLED = 1
const dateArray = new Date()
    .toISOString()
    .slice(0, 10)
    .split('-')

export default {
    data() {
        return {
            name: '',
            email: '',
            orgTitle: '',
            affiliation: '',
            projectGroupId: 0,
            projectReportId: 0,
            comments: '',
            currentStatus: null,
            requestError: '',
            errors: [],

            startMonth: dateArray[1],
            endMonth: dateArray[1],
            monthOptions: [
                { text: 'January', value: '01' },
                { text: 'February', value: '02' },
                { text: 'March', value: '03' },
                { text: 'April', value: '04' },
                { text: 'May', value: '05' },
                { text: 'June', value: '06' },
                { text: 'July', value: '07' },
                { text: 'August', value: '08' },
                { text: 'September', value: '09' },
                { text: 'October', value: '10' },
                { text: 'November', value: '11' },
                { text: 'December', value: '12' }
            ],
            startDay: dateArray[2],
            endDay: dateArray[2],
            dayOptions: [
                { text: '1', value: '01' },
                { text: '2', value: '02' },
                { text: '3', value: '03' },
                { text: '4', value: '04' },
                { text: '5', value: '05' },
                { text: '6', value: '06' },
                { text: '7', value: '07' },
                { text: '8', value: '08' },
                { text: '9', value: '09' },
                { text: '10', value: '10' },
                { text: '11', value: '11' },
                { text: '12', value: '12' },
                { text: '13', value: '13' },
                { text: '14', value: '14' },
                { text: '15', value: '15' },
                { text: '16', value: '16' },
                { text: '17', value: '17' },
                { text: '18', value: '18' },
                { text: '19', value: '19' },
                { text: '20', value: '20' },
                { text: '21', value: '21' },
                { text: '22', value: '22' },
                { text: '23', value: '23' },
                { text: '24', value: '24' },
                { text: '25', value: '25' },
                { text: '26', value: '26' },
                { text: '27', value: '27' },
                { text: '28', value: '28' },
                { text: '29', value: '29' },
                { text: '30', value: '30' },
                { text: '31', value: '31' }
            ],
            startYear: dateArray[0],
            endYear: dateArray[0],
            yearOptions: [
                // { text: '2019', value: '2019' },
                // { text: '2020', value: '2020' }
            ],

            projectGroup: '',
            projectGroups: [],
            projects: [],
            projectFieldState: FIELD_DISABLED,

            termsOfUse: ''
        }
    },
    created() {
        this.reset()

        let currentYear = new Date().getFullYear()
        for (var i = 1996; i <= currentYear; i++) {
            this.yearOptions.push({ text: '' + i, value: '' + i })
        }

        axios
            .get(process.env.VUE_APP_API_DOMAIN + '/projects/groups')
            .then(({ data }) => {
                this.projectGroups = data.projectGroups
            })

        axios
            .get(process.env.VUE_APP_DRUPAL_API_DOMAIN + '/api/terms-of-use')
            .then(({ data }) => {
                // console.log(data[0].body[0])
                this.termsOfUse = data[0].body[0].value
            })
    },
    computed: {
        isInitial() {
            return this.currentStatus === STATUS_INITIAL
        },
        isSaving() {
            return this.currentStatus === STATUS_SAVING
        },
        isSuccess() {
            return this.currentStatus === STATUS_SUCCESS
        },
        isFailed() {
            return this.currentStatus === STATUS_FAILED
        },
        projectsEnabled() {
            return this.projectFieldState === FIELD_ENABLED
        },
        projectsDisabled() {
            return this.projectFieldState === FIELD_DISABLED
        }
    },
    methods: {
        reset() {
            // reset form to initial state
            this.currentStatus = STATUS_INITIAL
            this.projectFieldState = FIELD_DISABLED
        },
        projectGroupChange(projectGroupId) {
            console.log(projectGroupId)
            this.projectFieldState = FIELD_DISABLED
            axios
                .get(
                    process.env.VUE_APP_API_DOMAIN +
                        '/projects/' +
                        projectGroupId
                )
                .then(({ data }) => {
                    this.projects = data.projects
                    if (this.projects.length > 1) {
                        this.projectReportId = 0
                        this.projectFieldState = FIELD_ENABLED
                    } else {
                        this.projectReportId = data.projects[0].id
                        this.projectFieldState = FIELD_DISABLED
                    }
                    console.log(this.projectsDisabled)
                })
        },
        request() {
            this.errors = []
            if (this.projectGroupId == 0) {
                this.errors.push('Project Group is a required field.')
            }
            if (this.projectGroupId == null) {
                this.errors.push('Project Group is a required field.')
            }
            if (this.name.length < 2) {
                this.errors.push('Name is a required field.')
            }
            if (this.comments.length < 2) {
                this.errors.push('"Nature of research" is a required field.')
            }
            if (this.orgTitle.length < 2) {
                this.errors.push('Organizational Title is a required field.')
            }
            if (this.affiliation.length < 2) {
                this.errors.push(
                    'Institutional Affiliation is a required field.'
                )
            }
            if (this.email.length < 7) {
                this.errors.push('Email is a required field.')
            }
            if (!this.validEmail(this.email)) {
                this.errors.push('Please enter a valid email address.')
            }
            if (this.errors.length > 0) {
                return false
            }

            this.currentStatus = STATUS_SAVING

            // fd.append(
            //     'when',
            //     this.whenYear + '-' + this.whenMonth + '-' + this.whenDay
            // )
            return axios
                .post(
                    process.env.VUE_APP_API_DOMAIN + '/request-data',
                    {
                        projectGroupId: this.projectGroupId,
                        projectReportId: this.projectReportId,
                        name: this.name,
                        email: this.email,
                        orgTitle: this.orgTitle,
                        affiliation: this.affiliation,
                        startDate:
                            this.startYear +
                            '-' +
                            this.startMonth +
                            '-' +
                            this.startDay,
                        endDate:
                            this.endYear +
                            '-' +
                            this.endMonth +
                            '-' +
                            this.endDay,
                        comments: this.comments
                    },
                    {
                        headers: {
                            Accept: 'application/json'
                        }
                    }
                )
                .then(response => {
                    console.log(response)
                    this.currentStatus = STATUS_SUCCESS
                })
                .catch(err => {
                    console.log(err)
                    this.requestError = err.response.data.message
                    this.currentStatus = STATUS_FAILED
                })
        },
        validEmail(email) {
            var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return re.test(email)
        }
    }
}
</script>
